"use client";

import { useEffect, useRef, useState } from "react";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon";
import Button from "./Button";

type ExpandableTextProps = {
  children: React.ReactNode;
  collapsedHeight: number;
  className?: string;
  contentClassName?: string;
  fadeClassName?: string;
  buttonClassName?: string;
  expandedButtonClassName?: string;
  iconWrapperClassName?: string;
  expandedIconWrapperClassName?: string;
  chevronColor?: string;
  expandLabel?: string;
  collapseLabel?: string;
};

export default function ExpandableText({
  children,
  collapsedHeight,
  className = "",
  contentClassName = "",
  fadeClassName = "",
  buttonClassName = "",
  expandedButtonClassName = "",
  iconWrapperClassName = "",
  expandedIconWrapperClassName = "",
  chevronColor = "currentColor",
  expandLabel = "Read more",
  collapseLabel = "Read less",
}: ExpandableTextProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(collapsedHeight);
  const [showToggle, setShowToggle] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const measure = () => {
      const nextHeight = content.scrollHeight;
      setContentHeight(nextHeight);
      setShowToggle(nextHeight > collapsedHeight + 4);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(content);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [collapsedHeight, children]);

  return (
    <div className={className}>
      <div
        className="relative overflow-hidden transition-[max-height] duration-300 ease-out"
        style={{ maxHeight: expanded || !showToggle ? `${contentHeight}px` : `${collapsedHeight}px` }}
      >
        <div ref={contentRef} className={contentClassName}>
          {children}
        </div>
        {showToggle && !expanded ? (
          <div className={`pointer-events-none absolute inset-x-0 bottom-0 ${fadeClassName}`} aria-hidden />
        ) : null}
      </div>

      {showToggle ? (
        <Button
          raw
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className={`${buttonClassName} ${expanded ? expandedButtonClassName : ""}`.trim()}
        >
          <span>{expanded ? collapseLabel : expandLabel}</span>
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current/20 bg-current/5 transition-colors ${
              expanded ? expandedIconWrapperClassName : iconWrapperClassName
            }`.trim()}
          >
            <ChevronDownIcon
              size={16}
              color={chevronColor}
              className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
            />
          </span>
        </Button>
      ) : null}
    </div>
  );
}
